import React from "react";

import { CheckIcon } from "@heroicons/react/outline";
import Image from "next/image";
import Link from "next/link";

import EnerfloBestInShow2024 from "public/images/enerflo-2024-top-products-best-in-show-residential.png";
import ShieldIcon from "public/images/icons/solar-financing-operating-system/Compliance.svg";
import ClockIcon from "public/images/icons/solar-financing-operating-system/Go-live-fast.svg";
import NetworkIcon from "public/images/icons/solar-financing-operating-system/Nationwide-network-blue.svg";
import TimelinesIcon from "public/images/icons/solar-financing-operating-system/Waveform-blue.svg";
import EnerfloBestInShow2025 from "public/images/solar-power-world-2025-top-products-best-in-show-residential-enerflo.png";

import Heading from "components/heading";
import HeroSection from "components/hero-section";

const features = [
  {
    title: "62% faster install timelines vs. peers",
    description:
      "An independent Ohm Analytics study found that installers on Enerflo reduced install timelines 62% relative to peers — even as volumes nearly doubled. Faster cycle times mean better pull-through rates and stronger portfolio performance for your fund.",
    icon: TimelinesIcon,
  },
  {
    title: "Compliance infrastructure already built",
    description:
      "SOC 2 Type II certification, audit trails, license validation, and fraud tooling took years to build and harden. You inherit all of it on day one — no build, no legal exposure from standing it up yourself.",
    icon: ShieldIcon,
  },
  {
    title: "Nationwide installer network from day one",
    description:
      "Access Enerflo's active installer network nationwide immediately — no cold outreach, no dealer onboarding from scratch. Your program can begin originating volume on day one.",
    icon: NetworkIcon,
  },
  {
    title: "Live in weeks — dev hours included",
    description:
      "Most solar financiers go live in weeks, not months. Integration engineering is covered by Enerflo — no hourly invoices for the build, and no surprise bills after launch.",
    icon: ClockIcon,
  },
];

const FeatureBlock = ({ title, description, icon }: typeof features[number]) => (
  <li className="p-4 md:p-8 bg-gray-100">
    <figure className="h-12 w-12 bg-white rounded-lg p-1 border border-primary mb-3 mx-auto md:mx-0">
      <Image src={icon} alt={`${title} - Icon`} />
    </figure>
    <Heading tag="h3" size="h6" className="text-navy mb-4">
      {title}
    </Heading>
    <p className="text-gray-600">{description}</p>
  </li>
);

const supportedTypes = ["Solar Loan", "Solar Lease", "Power Purchase Agreement", "Prepaid PPA"];

const SupportedProductTypes = () => (
  <div className="my-4 space-y-2 text-center">
    <HeroSection.Tagline className="text-primary mb-4">Supported Solar Financing Product Types</HeroSection.Tagline>
    <ul className="text-navy font-semibold text-left inline-flex flex-col xs:flex-row gap-x-6 gap-y-4 flex-wrap justify-center">
      {supportedTypes.map(item => (
        <li key={item} className="text-lg md:text-2xl flex gap-2 items-start xs:items-center">
          <div className="inline-block p-1 rounded-full shadow-md bg-primary text-white">
            <CheckIcon className="h-4 w-4" />
          </div>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

const BestInShowImages = () => (
  <div className="flex flex-col gap-x-16 md:gap-x-24 gap-y-6 xs:flex-row justify-center">
    <div>
      <Link href="/resources/enerflo-wins-solar-power-worlds-2025-best-in-show-top-product-award-residential">
        <a className="inline-block">
          <Image
            src={EnerfloBestInShow2025}
            alt="Enerflo Best In Show Award 2025"
            title="Enerflo Best In Show Award 2025"
            height={150}
            width={150}
          />
        </a>
      </Link>
    </div>

    <div>
      <Link href="/resources/enerflo-wins-solar-power-world-2024-residential-best-in-show">
        <a className="inline-block">
          <Image
            src={EnerfloBestInShow2024}
            alt="Enerflo Best In Show Award 2024"
            title="Enerflo Best In Show Award 2024"
            height={150}
            width={150}
          />
        </a>
      </Link>
    </div>
  </div>
);

const title = "The Solar Financing Operating System";

const OperatingSystem = () => (
  <HeroSection title={title} className="bg-white">
    <HeroSection.Content className="space-y-14">
      <header className="text-center mb-12">
        <HeroSection.Tagline className="text-primary mb-4">{title}</HeroSection.Tagline>
        <Heading tag="h2" className="mb-6">
          <span>8 Years Building Residential Solar Financing Infrastructure. $15B+ Sold.</span>
          <br />
          <span className="text-primary max-w-4xl mx-auto inline-block mt-2">Live on Enerflo in Weeks, Not Years.</span>
        </Heading>
        <p className="text-lg text-gray-600 max-w-5xl mx-auto">
          Building solar financing infrastructure from scratch — solar loan origination software, TPO platform tools,
          compliance, fraud management — means years of trial, rework, and regulatory exposure before you originate a
          single deal. Enerflo’s solar financing operating system was built iteratively over 8 years through hundreds of
          integrations and billions in residential solar transactions, so banks, capital providers, solar lenders, and
          TPO financiers can launch with a proven, white-label platform and start gaining ground from day one. When you
          launch with Enerflo, your product is immediately pushed to our existing nationwide installer network — already
          trained, already active, and ready to originate on your behalf from day one.
        </p>
      </header>

      <BestInShowImages />
      <SupportedProductTypes />

      <section>
        <ul className="rounded-2xl bg-white grid grid-cols-1 md:grid-cols-2 gap-0.5 overflow-hidden">
          {features.map(feature => (
            <FeatureBlock key={feature.title} {...feature} />
          ))}
        </ul>
      </section>
    </HeroSection.Content>
  </HeroSection>
);

export default OperatingSystem;
